import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { EMPTY, Observable, of } from 'rxjs';
import { translate } from '@jsverse/transloco';
import { switchMap, tap, map, catchError, finalize } from 'rxjs/operators';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { Router } from '@angular/router';
import { ICreateSlotProductRequest, IGetSlotProductResponse, IUpdateSlotProductRequest } from 'src/app/cabinet/models/slot-product';
import { SlotProductService } from 'src/app/cabinet/services/slot-product.service';

export interface SlotProductState {
  slotProducts: IGetSlotProductResponse[];
  loading: boolean;
}

@Injectable()
export class SlotProductStore extends ComponentStore<SlotProductState> {

  // Initial state
  constructor(
    private slotProductService: SlotProductService,
    private appnotificationService: AppNotificationService,
    private router: Router
  ) {
    super({ slotProducts: null, loading: false });
  }

  // Selectors
  readonly loading$ = this.select((state) => state.loading);
  readonly slotProducts$: Observable<IGetSlotProductResponse[]> = this.select(state => state.slotProducts);

  // Effect to get slot products by slot id
  readonly getSlotProductsBySlotId = this.effect<string>(slotId$ =>
    slotId$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap(slotId =>
        this.slotProductService.getSlotProductsBySlot({pageNumber: 0, pageSize: 25, slotId: slotId}).pipe(
          tap(slots => {
            // Update the state with selected slots and their count
            this.patchState({ slotProducts: slots.data, loading: false });
          }),
          catchError(err => {
            console.error('Failed to load slot products:', err);
            this.patchState({ loading: false });
            return of([]); // Return an empty array on error
          })
        )
      )
    )
  );

  // Effect to create a new slot product
  readonly createSlotProduct = this.effect<ICreateSlotProductRequest>(slot$ =>
    slot$.pipe(
      switchMap(slot =>
        this.slotProductService.createSlotProduct(slot).pipe(
          tap({
            next: (createdSlot) => {
              if (createdSlot) {
                // Update the state with the newly created slot
                this.patchState(state => ({
                  slotProducts: [...state.slotProducts, createdSlot], // Append the new slot
                }));

                this.appnotificationService.notification$.next({
                  message: translate('slots.createSuccess'),
                  style: 'success'
                });
              }
            },
            error: (e) => console.error(e),
          }),
          catchError(() => EMPTY),
        )
      )
    )
  );

  // Effect to delete a slot product
  readonly deleteSlotProduct = this.effect<string>(slotId$ =>
    slotId$.pipe(
      switchMap(slotId =>
        this.slotProductService.deleteSlotProduct(slotId).pipe(
          tap({
            next: () => {
              // Remove the slot from the state
              this.patchState(state => ({
                slotProducts: state.slotProducts.filter(slot => slot.id !== slotId),
              }));

              this.appnotificationService.notification$.next({
                message: translate('slotProducts.deleteSuccess'),
                style: 'success'
              });
            },
            error: (err) => {
              this.appnotificationService.notification$.next({
                message: translate('slotProducts.deleteFailed'),
                style: 'error'
              });
            }
          })
        )
      )
    )
  );

  // Effect to update a slot
  readonly updateSlotProduct = this.effect<IUpdateSlotProductRequest>(slot$ =>
    slot$.pipe(
      switchMap(slot =>
        this.slotProductService.updateSlotProduct(slot).pipe(
          tap({
            next: (updatedSlot) => {
              if (updatedSlot) {
                // Update the state with the updated slot
                this.patchState(state => ({
                  slotProducts: [...state.slotProducts.map(existingSlot =>
                    existingSlot.id === updatedSlot.id ? updatedSlot : existingSlot
                  )]
                }));
                

                // Show success notification
                this.appnotificationService.notification$.next({
                  message: translate('slotProducts.updateSuccess'),
                  style: 'success'
                });
              }
            },
            error: (err) => {
              this.appnotificationService.notification$.next({
                message: translate('slotProducts.updateFailed'),
                style: 'error'
              });
            }
          })
        )
      )
    )
  );



  // Updater to set the loading state
  readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
    error: undefined, // Clear error when starting a new process
  }));

  readonly logStateChanges = this.effect<void>(trigger$ =>
    trigger$.pipe(
      tap(() => {
        this.state$.subscribe(state => console.log('ComponentStore State:', state));
      })
    )
  );

}
